import { findChallengeById } from '@devlearn/content';
import { computeUnlocks, lockedBy, type ProgressResponse } from '@devlearn/shared';
import { ruleStructureOf } from './structure';

export type ChallengeAccess =
  | { kind: 'open' }
  | { kind: 'sign-in-required' }
  | { kind: 'locked'; requires: { id: string; title: string; path: string } | null };

/** Guest unlocks use the shared rules with `guest: true` (only `isGuest` challenges). */
export function guestUnlocks(trackSlug: string, completedIds: Iterable<string>) {
  return computeUnlocks(ruleStructureOf(trackSlug), completedIds, { guest: true });
}

function describe(id: string | null) {
  const loc = id ? findChallengeById(id) : undefined;
  if (!loc) return null;
  return {
    id: loc.challenge.id,
    title: loc.challenge.title,
    path: `/learn/${loc.track.slug}/${loc.chapter.slug}/${loc.challenge.slug}`,
  };
}

/** Can this learner open the challenge? (PRODUCT.md §5.1). `devUnlock` bypasses gates in dev builds. */
export function challengeAccess(args: {
  trackSlug: string;
  challengeId: string;
  isGuest: boolean;
  session: 'guest' | 'authenticated';
  progress?: ProgressResponse | undefined;
  devUnlock?: boolean;
}): ChallengeAccess {
  if (args.devUnlock) return { kind: 'open' };
  if (args.session === 'guest') return args.isGuest ? { kind: 'open' } : { kind: 'sign-in-required' };
  const state = args.progress?.challenges.find((c) => c.id === args.challengeId)?.state;
  if (!args.progress || state !== 'locked') return { kind: 'open' };
  const completed = args.progress.challenges.filter((c) => c.state === 'completed').map((c) => c.id);
  return {
    kind: 'locked',
    requires: describe(lockedBy(ruleStructureOf(args.trackSlug), completed, args.challengeId)),
  };
}
